import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { consoleLogInDev } from 'shared';
import { UpperDaysOfWeek, daysOfTheWeek } from 'shared/Dates';
import { Address } from 'models';
import {
    getPastWorkers,
    getPreferredWorkers,
    PastWorkerDTO,
    PreferredWorkerDTO,
} from 'parent-portal/forms/components/pages/ProviderPreferencesPage/preferredWorkersApi';
import type { DayOfWeekSelections } from 'parent-portal/forms/JobRequestTypes';
import { PostJobLocationState, RequestedWorker, ShiftTimes } from '../formTypes';
import { OngoingJobResponse } from '../postJobApi';
import { isCompleteDate } from './useScheduleFields';

interface RequestedWorkersParams {
    presets: PostJobLocationState | undefined;
    addresses: Address[];
    selectedAddressIds: number[];
    startDate: string;
    endDate: string;
    selectedDays: UpperDaysOfWeek[];
    times: ShiftTimes;
}

export function useRequestedWorkers({
    presets,
    addresses,
    selectedAddressIds,
    startDate,
    endDate,
    selectedDays,
    times,
}: RequestedWorkersParams) {
    const [presetWorkers] = useState<RequestedWorker[]>(() => [
        ...(presets?.requestedWorkers?.map((worker) => ({ ...worker, spotlight: false })) ?? []),
        ...(presets?.spotlightWorkers?.map((worker) => ({ ...worker, spotlight: true })) ?? []),
    ]);

    const [pastWorkers, setPastWorkers] = useState<PastWorkerDTO[]>([]);
    const [preferredWorkers, setPreferredWorkers] = useState<PreferredWorkerDTO[]>([]);
    const [spotlightWorkers, setSpotlightWorkers] = useState<RequestedWorker[]>(
        presetWorkers.filter((worker) => worker.spotlight),
    );
    const [requestedWorkers, setRequestedWorkers] = useState<RequestedWorker[]>(presetWorkers);

    useEffect(() => {
        if ((startDate && !isCompleteDate(startDate)) || (endDate && !isCompleteDate(endDate))) return undefined;
        let cancelled = false;
        const addressIds = selectedAddressIds.length > 0 ? selectedAddressIds : addresses.map((a) => a.id);
        const toJobTime = (date: string, time: string) => {
            const parsed = date ? moment(`${date} ${time}`) : null;
            return parsed?.isValid() ? parsed : null;
        };
        const jobTimes = {
            start: toJobTime(startDate, times.start),
            end: toJobTime(endDate, times.end),
            days_of_week: Object.fromEntries(
                daysOfTheWeek.map((day) => [day, { selected: selectedDays.includes(day) }]),
            ) as unknown as DayOfWeekSelections,
        };
        const loadAllPreferredWorkers = async () => {
            const first = await getPreferredWorkers(1, addressIds, jobTimes, 100);
            let results = first.results;
            for (let page = 2; page <= first.num_pages; page += 1) {
                const next = await getPreferredWorkers(page, addressIds, jobTimes, 100);
                results = [...results, ...next.results];
            }
            return results;
        };
        loadAllPreferredWorkers()
            .then((results) => {
                if (!cancelled) setPreferredWorkers(results);
            })
            .catch(consoleLogInDev);
        getPastWorkers(jobTimes, addressIds)
            .then((workers) => {
                if (!cancelled) setPastWorkers(workers);
            })
            .catch(consoleLogInDev);
        return () => {
            cancelled = true;
        };
    }, [selectedAddressIds, addresses, startDate, endDate, selectedDays]);

    const toggleRequestedWorker = (worker: RequestedWorker) => {
        setRequestedWorkers((current) =>
            current.some((w) => w.id === worker.id && w.spotlight === worker.spotlight)
                ? current.filter((w) => !(w.id === worker.id && w.spotlight === worker.spotlight))
                : [...current, worker],
        );
    };

    const applyJob = (job: OngoingJobResponse) => {
        const workers: RequestedWorker[] = job.requested_workers ?? [];
        if (workers.length === 0) return;
        setRequestedWorkers(workers);
        setSpotlightWorkers((current) => {
            const known = new Set(current.map((worker) => worker.id));
            return [...current, ...workers.filter((worker) => worker.spotlight && !known.has(worker.id))];
        });
    };

    const snapshot = useMemo(() => ({ requestedWorkers }), [requestedWorkers]);

    return {
        pastWorkers,
        preferredWorkers,
        spotlightWorkers,
        requestedWorkers,
        toggleRequestedWorker,
        applyJob,
        snapshot,
    };
}
